import { useState, useEffect } from "react";
import { Truck, Copy, Download, PackageCheck, Clock, Search, Instagram, Lock, ChevronDown, FileText, FileSpreadsheet, Package } from "lucide-react";
import { toast } from "sonner";
import api, { invalidateCache } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/**
 * ShipmentsPanel.jsx
 * ------------------------------------------------------------------
 * Dedicated shipping-details tab, separate from Orders (which is
 * already full on desktop and mobile). Pulls from
 * GET /api/merchant/shipments -- shows AWB, courier, tracking link,
 * label, and pickup status per paid order.
 *
 * Sep 2026: courier booking moved from automatic (right after payment)
 * to merchant-triggered ("Ship this order") -- this avoids paying for
 * an AWB/label on an order that later changes before it's packed, and
 * means Shiprocket/Shippo always see the order's current contents at
 * the moment the merchant actually ships it, not what it looked like
 * at checkout. One button now does everything for both providers:
 * books the courier, buys the label, requests pickup where the
 * courier/provider supports it (always for Shiprocket; USPS/DHL
 * Express only for Shippo -- Shippo has no pickup API for other
 * carriers), marks the order shipped, and fires the shipped email/DM.
 * See POST /merchant/shipments/ship.
 * ------------------------------------------------------------------
 */

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "awb_assigned", label: "Ready to pack" },
  { value: "pending_awb", label: "AWB pending" },
  { value: "shipped", label: "Shipped" },
  { value: "not_shipped", label: "Not shipped" },
];

const FREE_PLAN_ROW_CAP = 15;

const DELIVERY_STATUS_META = {
  delivered: { icon: "✓", label: "Delivered", pillClassName: "bg-mint text-ink" },
  rto: { icon: "↩", label: "Returned to origin", pillClassName: "bg-peach text-ink" },
  failed: { icon: "⚠", label: "Delivery failed", pillClassName: "bg-hotpink/10 text-hotpink" },
};

const RETURN_STATUS_META = {
  requested: { label: "Return requested", className: "bg-peach text-ink" },
  approved: { label: "Returned", className: "bg-mint text-ink" },
  rejected: { label: "Return rejected", className: "bg-hotpink/10 text-hotpink" },
};

const EXCHANGE_STATUS_META = {
  requested: { label: "Exchange requested", className: "bg-[#4C1D95]/10 text-[#4C1D95]" },
  approved: { label: "Exchanged", className: "bg-mint text-ink" },
  rejected: { label: "Exchange rejected", className: "bg-hotpink/10 text-hotpink" },
};

function ShipmentItemsToggle({ items }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="relative mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[10px] font-semibold text-hotpink underline decoration-dotted"
      >
        {open ? "Hide items" : `Items (${items.length})`}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-64 space-y-1.5 rounded-xl border border-ink/10 bg-white p-3 text-left text-[11px] text-ink/70 shadow-lg">
          {items.map((it) => {
            const returnMeta = it.return_request_status ? RETURN_STATUS_META[it.return_request_status] : null;
            const exchangeMeta = it.exchange_request_status ? EXCHANGE_STATUS_META[it.exchange_request_status] : null;
            const sizes = it.exchange_to_size
              ? ` (${it.exchange_from_size ? `${it.exchange_from_size} → ` : ""}${it.exchange_to_size})`
              : "";
            return (
              <div key={it.id} className="flex items-start justify-between gap-2">
                <span className="max-w-[150px]">
                  {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                </span>
                <span className="flex flex-none flex-col items-end gap-1">
                  {returnMeta && (
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${returnMeta.className}`}>
                      {returnMeta.label}
                    </span>
                  )}
                  {exchangeMeta && (
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${exchangeMeta.className}`}>
                      ⇄ {exchangeMeta.label}{sizes}
                    </span>
                  )}
                  {!returnMeta && !exchangeMeta && <span className="text-[10px] text-ink/30">—</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ order }) {
  if (order.is_digital_order) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-lilac px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
        💾 Digital
      </span>
    );
  }
  if (order.shipped_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-mint px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
        <PackageCheck size={12} /> Shipped
      </span>
    );
  }
  if (order.shipment_status === "awb_assigned") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-peach px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
        <Truck size={12} /> Ready to pack
      </span>
    );
  }
  if (order.shipment_status === "pending_awb") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-lilac px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
        <Clock size={12} /> AWB pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-ink/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink/50">
      Not shipped
    </span>
  );
}

function ReturnFlag({ items }) {
  const active = (items || []).some(
    (it) => it.return_request_status === "requested" || it.return_request_status === "approved"
  );
  if (!active) return null;
  return (
    <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-peach px-2 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-ink">
      ↩ Return on this order
    </span>
  );
}

// order.exchange_status is a single rollup already computed server-side
// (Get Shipments query) -- only flag it here when a decision is still
// pending; an approved/rejected exchange is resolved and doesn't need to
// grab attention on this row.
function ExchangeFlag({ exchangeStatus }) {
  if (exchangeStatus !== "exchange_requested") return null;
  return (
    <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#4C1D95]/10 px-2 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-[#4C1D95]">
      ⇄ Exchange on this order
    </span>
  );
}

// Replaces the old "Schedule pickup" button. Courier booking now happens at
// the moment this is clicked (not automatically after payment), so this
// shows on ANY unshipped order regardless of whether awb_code/courier_name
// are set yet -- they won't be, until this fires. One click books the
// courier, buys the label, requests pickup where possible, and marks the
// order shipped -- for both Shiprocket and Shippo.
function ShipButton({ order, onShip, shipping, courierConnected }) {
  // Digital-only orders are delivered automatically. There is nothing to ship or book a courier for.
  if (order.is_digital_order) {
    return (
      <span className="mt-1 inline-block max-w-[180px] text-center text-[10px] font-semibold leading-snug text-ink/40">
        Digital only · no shipping needed
      </span>
    );
  }
  if (order.awb_code) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-ink/50">
        <PackageCheck size={11} /> Shipped
      </span>
    );
  }
  if (order.shipped_at) {
    return (
      <button
        onClick={() => onShip(order.id)}
        disabled={shipping}
        className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-cream hover:bg-ink/85 disabled:opacity-50"
        title="Marked shipped manually — no courier booked yet. Click to book the real courier and send tracking."
      >
        <Truck size={12} /> {shipping ? "Shipping…" : "Book real courier"}
      </button>
    );
  }
  if (!courierConnected) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-ink/[0.03] px-3 py-1.5 text-xs font-semibold text-ink/40"
        title="Connect a courier in Courier settings to enable shipping"
      >
        <Lock size={11} /> No courier connected
      </span>
    );
  }
  return (
    <button
      onClick={() => onShip(order.id)}
      disabled={shipping}
      className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-cream hover:bg-ink/85 disabled:opacity-50"
    >
      <Truck size={12} /> {shipping ? "Shipping…" : "Ship this order"}
    </button>
  );
}

function igProfileUrl(handle) {
  if (!handle) return null;
  const clean = String(handle).replace(/^@/, "").trim();
  return clean ? `https://instagram.com/${clean}` : null;
}

function ProfilePic({ src, className, iconSize = 14 }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <span className={`grid place-items-center rounded-full bg-sunset-gradient text-white ${className}`}>
        <Instagram size={iconSize} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className={`rounded-full object-cover ${className}`}
      onError={() => setBroken(true)}
    />
  );
}

function StatPill({ label, value, icon: Icon, accent }) {
  return (
    <div className={`flex items-center gap-3 rounded-2xl border border-ink/10 p-4 ${accent}`}>
      <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-white/70 text-ink">
        <Icon size={16} strokeWidth={2.4} />
      </span>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-ink/50">{label}</p>
        <p className="font-display text-xl font-black tracking-tight">{value}</p>
      </div>
    </div>
  );
}

function FreePlanRowCap({ shown, total }) {
  const hasMore = total > shown;
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-5">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
          <Lock size={18} />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Free plan</p>
          <p className="mt-0.5 text-sm text-ink/70">
            Showing your {shown} most recent shipments
            {hasMore ? <> — <strong className="text-ink">{total - shown} more</strong> hidden on Free.</> : "."}
          </p>
        </div>
      </div>
      <a href="/plans" className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-center text-sm font-semibold text-white">
        Upgrade to Starter to see all
      </a>
    </div>
  );
}

function ExportMenu({ search, status, plan }) {
  // Locked below Growth -- same gate as Orders/Customers/Returns export.
  const exportLocked = !(plan === "growth" || plan === "pro");
  const download = async (format) => {
    try {
      const params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (status) params.push(`shipment_status=${encodeURIComponent(status)}`);
      const q = params.length ? `?${params.join("&")}` : "";
      const res = await api.get(`/merchant/shipments/export/${format}${q}`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `shipments.${format}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloading shipments ${format.toUpperCase()}…`);
    } catch (e) {
      toast.error(`Couldn't download shipments ${format.toUpperCase()} — try again.`);
    }
  };

  if (exportLocked) {
    return (
      <a
        href="/plans"
        title="Export is available on Growth and Pro plans"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-2 text-sm font-semibold text-ink/50 md:gap-2 md:px-5"
      >
        <Lock size={14} /> <span className="hidden sm:inline">Export</span>
      </a>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="cta-glow inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sunset-gradient px-3 py-2 text-sm font-semibold text-white md:gap-2 md:px-5">
          <Download size={14} /> <span className="hidden sm:inline">Export</span> <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={() => download("csv")} className="cursor-pointer">
          <FileText size={14} className="mr-2" /> Download CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("xlsx")} className="cursor-pointer">
          <FileSpreadsheet size={14} className="mr-2" /> Download Excel (.xlsx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ShipmentsPanel({ merchantReady, plan, courierConnected }) {
  const isFree = plan === "free";
  const showDeliveryTracking = plan === "starter" || plan === "growth" || plan === "pro";
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 20, stats: { awaiting_courier: 0, ready_to_ship: 0, shipped: 0 } });
  const [loading, setLoading] = useState(true);
  const [shippingOrder, setShippingOrder] = useState({});

  const fetchShipments = () => {
    if (!merchantReady) return;
    setLoading(true);
    const q = query ? `&search=${encodeURIComponent(query)}` : "";
    const s = status ? `&shipment_status=${encodeURIComponent(status)}` : "";
    const effectivePage = isFree ? 1 : page;
    const effectiveLimit = isFree ? FREE_PLAN_ROW_CAP : 20;
    // Intentionally uncached (plain api.get, not cachedGet) -- shipment
    // status changes the moment a merchant acts (Ship this order), and a
    // stale cache here shows the wrong state.
    return api
      .get(`/merchant/shipments?page=${effectivePage}&limit=${effectiveLimit}${s}${q}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load shipments. Try refreshing."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
  fetchShipments();
}, [page, status, query, merchantReady, isFree]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Tracking link copied.");
  };

  // Books the courier, buys the label, requests pickup where the carrier
  // supports it, and marks the order shipped -- all in this one call, for
  // either provider. Re-fetches the row afterward (rather than patching
  // fields in place) since courier_name/awb_code/label_url/tracking_url are
  // all unknown client-side until this call actually books the shipment.
  const shipOrder = async (orderId) => {
    setShippingOrder((s) => ({ ...s, [orderId]: true }));
    try {
      const { data: res } = await api.post("/merchant/shipments/ship", { order_id: orderId });
      toast.success(res.message || "Order shipped.");
      invalidateCache("/merchant/orders");
      await fetchShipments();
    } catch (e) {
      const msg = e?.response?.data?.message || "Couldn't ship this order — try again.";
      toast.error(msg);
    } finally {
      setShippingOrder((s) => ({ ...s, [orderId]: false }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));
  const stats = data.stats || { awaiting_courier: 0, ready_to_ship: 0, shipped: 0 };

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="shipments-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Shipping</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            AWB, courier &amp; tracking
          </h2>
          <p className="mt-1 text-sm text-ink/60">
            Every paid order's shipping details, separate from Orders so there's room to see it all.
          </p>
        </div>
        <div className="flex items-center gap-2 md:flex-wrap md:gap-3">
          <form onSubmit={applySearch} className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Order #, customer, or AWB"
                data-testid="shipments-search-input"
                className="w-full rounded-full border border-ink/15 bg-white py-2 pl-9 pr-3 text-sm focus:border-hotpink focus:outline-none md:w-64"
              />
            </div>
            <button
              type="submit"
              data-testid="shipments-search-btn"
              className="shrink-0 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-cream hover:bg-ink/85"
            >
              Search
            </button>
          </form>
          <ExportMenu search={query} status={status} plan={plan} />
        </div>
      </div>

      {!courierConnected && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-ink/[0.03] p-5">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-ink/5 text-ink/50">
              <Lock size={18} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">No courier connected</p>
              <p className="mt-0.5 text-sm text-ink/70">
                Connect Shiprocket or Shippo to book couriers, buy labels, and ship orders straight from here.
              </p>
            </div>
          </div>
          <a href="/courier" className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-center text-sm font-semibold text-white">
            Connect a courier
          </a>
        </div>
      )}

      {/* Lightweight stat strip -- same query, window functions, no extra
          round trip. No Delivered/Failed bucket: not tracked in the DB yet. */}
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <StatPill label="Awaiting courier" value={stats.awaiting_courier} icon={Package} accent="bg-peach/40" />
        <StatPill label="Ready to ship" value={stats.ready_to_ship} icon={Truck} accent="bg-lilac/40" />
        <StatPill label="Shipped" value={stats.shipped} icon={PackageCheck} accent="bg-mint/40" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setStatus(f.value);
              setPage(1);
            }}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
              status === f.value ? "bg-ink text-cream" : "border border-ink/15 bg-white text-ink/60 hover:bg-ink/5"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[980px] table-fixed text-sm">
          <colgroup>
            <col className="w-[8%]" />
            <col className="w-[16%]" />
            <col className="w-[17%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[10%]" />
            <col className="w-[8%]" />
            <col className="w-[19%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Order #</th>
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Address</th>
              <th className="pb-3 pr-4">Courier</th>
              <th className="pb-3 pr-4">AWB</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">Label</th>
              <th className="pb-3 text-right">Tracking</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-ink/50">No shipments match this filter.</td></tr>
            ) : (
              data.items.map((o) => (
                <tr key={o.id} className="border-b border-ink/5" data-testid={`shipment-row-${o.id}`}>
                  <td className="py-3 pr-4 align-top font-mono text-xs">{o.order_number}</td>
                  <td className="py-3 pr-4 align-top">
                    <div className="flex items-center gap-2">
                      <ProfilePic src={o.profile_pic} className="h-7 w-7 flex-none" iconSize={12} />
                      <div className="min-w-0">
                        {o.customer_ig_handle ? (
                          <a
                            href={igProfileUrl(o.customer_ig_handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Open @${o.customer_ig_handle} on Instagram`}
                            className="block truncate font-semibold text-hotpink hover:underline"
                          >
                            {o.display_name || o.customer_name || "—"}
                          </a>
                        ) : (
                          <p className="truncate">{o.display_name || o.customer_name || "—"}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/70">
                    <p className="text-xs leading-relaxed">{o.address_str || "—"}</p>
                    <ShipmentItemsToggle items={o.items} />
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/70">{o.courier_name || "—"}</td>
                  <td className="py-3 pr-4 align-top font-mono text-xs text-ink/70 break-all">{o.awb_code || "—"}</td>
                  <td className="py-3 pr-4 align-top">
                    <StatusBadge order={o} />
                    {o.shipped_at && showDeliveryTracking && o.delivery_status && DELIVERY_STATUS_META[o.delivery_status] && (
                      <span
                        className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${DELIVERY_STATUS_META[o.delivery_status].pillClassName}`}
                        title={o.delivery_status_raw || undefined}
                      >
                        {DELIVERY_STATUS_META[o.delivery_status].icon} {DELIVERY_STATUS_META[o.delivery_status].label}
                      </span>
                    )}
                    <ReturnFlag items={o.items} />
                    <ExchangeFlag exchangeStatus={o.exchange_status} />
                  </td>
                  <td className="py-3 pr-4 align-top">
                    {o.label_url ? (
                      <a href={o.label_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-hotpink hover:underline">
                        <Download size={12} /> Download
                      </a>
                    ) : (
                      <span className="text-xs text-ink/30">—</span>
                    )}
                  </td>
                  <td className="py-3 align-top text-right">
                    <div className="flex flex-col items-end gap-1.5">
                      <ShipButton order={o} onShip={shipOrder} shipping={!!shippingOrder[o.id]} courierConnected={courierConnected} />
                      {o.tracking_url && (
                        <button
                          onClick={() => copyLink(o.tracking_url)}
                          className="inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
                        >
                          <Copy size={12} /> Copy
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-6 space-y-3 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">No shipments match this filter.</p>
        ) : (
          data.items.map((o) => (
            <div key={o.id} className="rounded-2xl border border-ink/10 p-4" data-testid={`shipment-card-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ProfilePic src={o.profile_pic} className="h-8 w-8 flex-none" iconSize={14} />
                  <div className="min-w-0">
                    <p className="font-mono text-xs font-semibold text-ink">{o.order_number}</p>
                    {o.customer_ig_handle ? (
                      <a
                        href={igProfileUrl(o.customer_ig_handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block truncate text-sm font-semibold text-hotpink hover:underline"
                      >
                        {o.display_name || o.customer_name || "—"}
                      </a>
                    ) : (
                      <p className="mt-0.5 truncate text-sm text-ink/60">{o.display_name || o.customer_name || "—"}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-none flex-col items-end gap-1">
                  <StatusBadge order={o} />
                  {o.shipped_at && showDeliveryTracking && o.delivery_status && DELIVERY_STATUS_META[o.delivery_status] && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${DELIVERY_STATUS_META[o.delivery_status].pillClassName}`}
                      title={o.delivery_status_raw || undefined}
                    >
                      {DELIVERY_STATUS_META[o.delivery_status].icon} {DELIVERY_STATUS_META[o.delivery_status].label}
                    </span>
                  )}
                  <ReturnFlag items={o.items} />
                  <ExchangeFlag exchangeStatus={o.exchange_status} />
                </div>
              </div>
              {o.address_str && <p className="mt-2 text-xs text-ink/60">{o.address_str}</p>}
              <ShipmentItemsToggle items={o.items} />
              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs text-ink/70">
                <dt className="text-ink/40">Courier</dt>
                <dd className="text-right">{o.courier_name || "—"}</dd>
                <dt className="text-ink/40">AWB</dt>
                <dd className="text-right font-mono">{o.awb_code || "—"}</dd>
              </dl>
                            <div className="mt-3 flex flex-wrap gap-2">
                <ShipButton order={o} onShip={shipOrder} shipping={!!shippingOrder[o.id]} courierConnected={courierConnected} />
                {o.tracking_url && (
                  <button
                    onClick={() => copyLink(o.tracking_url)}
                    className="inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
                  >
                    <Copy size={12} /> Copy tracking link
                  </button>
                )}
                {o.label_url && (
                  <a
                    href={o.label_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-hotpink px-3 py-1.5 text-xs font-semibold text-white"
                  >
                    <Download size={12} /> Label
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isFree ? (
        <FreePlanRowCap shown={data.items.length} total={data.total} />
      ) : (
        <div className="mt-5 flex items-center justify-between border-t border-ink/10 pt-4 text-sm text-ink/70">
          <span>Page <span className="font-semibold text-ink">{page}</span> of {totalPages} · {data.total} total</span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
